<template lang="pug">
.session-print
  header.session-print__header
    .session-print__event
      h1.session-print__event-title {{ eventTitle }}
      p.session-print__event-dates {{ eventDates }}
    .session-print__section-label About This Session
  .session-print__rule

  .session-print__card.session-print-card
    p.session-print__eyebrow Viewing session detail
    h2.session-print__title {{ session.title }}

    .session-print__meta
      span.session-print__meta-item(v-if="session.startTime") {{ formatDate(session.startTime) }}
      span.session-print__meta-item(v-if="session.startTime")
        | {{ formatTime(session.startTime) }}
        template(v-if="session.endTime") &nbsp;–&nbsp;{{ formatTime(session.endTime) }}
      span.session-print__meta-item(v-if="session.room") {{ session.room }}

    .session-print__tags(v-if="hasTags")
      span.session-print__tag.session-print__tag--type(v-if="session.sessionFormat") {{ session.sessionFormat }}
      span.session-print__tag.session-print__tag--prereg(v-if="session.preregRequired") Pre-registration Required
      span.session-print__tag.session-print__tag--credit(v-if="creditType") {{ creditType }}
      span.session-print__tag.session-print__tag--topic(
        v-for="topic in session.topics"
        :key="topic") {{ topic }}

    .session-print__description(v-if="session.description" v-html="sanitizedDescription")

  section.session-print__speakers(v-if="session.speakers && session.speakers.length")
    h3.session-print__speakers-heading Speaker{{ session.speakers.length === 1 ? '' : 's' }}
    .session-print__speaker.session-print-speaker(v-for="s in session.speakers" :key="s.id || s.name")
      img.session-print__speaker-photo(v-if="s.photoUrl" :src="s.photoUrl" :alt="s.name")
      .session-print__speaker-info
        .session-print__speaker-name {{ s.name }}
        .session-print__speaker-title(v-if="s.title") {{ s.title }}
        div.session-print__speaker-bio(v-if="s.bio" v-html="bioHtml(s.bio)")
</template>

<script>
import { formatTime, formatDate } from './scheduleTimeUtils.js';
import { sanitizeHtml, bioToHtml } from './sanitizeHtml.js';

export default {
  name: 'SessionPrintView',

  props: {
    session: { type: Object, required: true },
  },

  computed: {
    eventTitle() {
      return (this.$root.config && this.$root.config.eventTitle) || '';
    },
    eventDates() {
      // Plumbed from the eventIntegration payload as raw ISO strings via Task A1's
      // schema extension (eventStartDate / eventEndDate). Compose a single human
      // label using the same date formatter the rest of the schedule uses.
      const cfg = this.$root.config || {};
      const start = formatDate(cfg.eventStartDate || '');
      const end = formatDate(cfg.eventEndDate || '');
      if (start && end && start !== end) return `${start} – ${end}`;
      return start || end || '';
    },
    creditType() {
      const v = this.session && this.session.creditType;
      if (!v || v === 'Not for Credit') return '';
      return v;
    },
    hasTags() {
      const s = this.session;
      return !!(s.sessionFormat || s.preregRequired || this.creditType || (s.topics && s.topics.length));
    },

    sanitizedDescription() {
      return sanitizeHtml(this.session && this.session.description);
    },
  },

  methods: { formatTime, formatDate, bioHtml: bioToHtml },
};
</script>

<style lang="scss" scoped>
// Print layout per Figma 2203:22081.
// Use only Print-Sans font and grayscale-friendly tag colors.

.session-print {
  font-family: 'Public Sans', sans-serif;
  color: black;
  padding: 24px 32px;
}

.session-print__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 16px;
}

.session-print__event-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px;
}

.session-print__event-dates {
  font-size: 14px;
  color: #616161;
  margin: 0;
}

.session-print__section-label {
  font-size: 20px;
  font-weight: 700;
}

.session-print__rule {
  border-bottom: 4px solid #163874;
  margin-bottom: 32px;
}

.session-print__card {
  padding: 24px 16px;
  margin-bottom: 32px;
}

.session-print__eyebrow {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #666664;
  margin: 0 0 4px;
}

.session-print__title {
  font-size: 26px;
  font-weight: 700;
  margin: 0 0 16px;
  line-height: 1.3;
}

.session-print__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #475569;
}

.session-print__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.session-print__tag {
  height: 24px;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  border: 1px solid #e6e8eb;
  background: #f0f0f0;
  color: #1e1e1f;

  &--credit { background: #cfe4f8; color: #163874; border-color: transparent; }
  &--prereg { background: #fef2f2; color: #93172e; border-color: transparent; }
  &--topic  { background: #e0f2f1; color: #00587c; border-color: transparent; }
}

.session-print__description {
  font-size: 16px;
  line-height: 1.5;
  color: #1e1e1f;
}

.session-print__speakers {
  margin-top: 24px;
}

.session-print__speakers-heading {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 16px;
}

.session-print__speaker {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 24px;
  page-break-inside: avoid;
}

.session-print__speaker-photo {
  width: 120px;
  height: 120px;
  border-radius: 9999px;
  object-fit: cover;
  flex-shrink: 0;
}

.session-print__speaker-name {
  font-size: 18px;
  font-weight: 700;
  color: #202020;
  margin-bottom: 4px;
}

.session-print__speaker-title {
  font-size: 16px;
  font-weight: 600;
  color: #666664;
  margin-bottom: 8px;
}

.session-print__speaker-bio {
  font-size: 16px;
  color: #666664;
  line-height: 1.5;

  ::v-deep p {
    margin: 0;
    &:not(:last-child) { margin-bottom: 0.75em; }
  }
}

@media print {
  .session-print__speaker-photo {
    display: block;
  }
}
</style>
